import { useState } from "react";
import { Link } from "react-router-dom";
import VendorLogo from "../components/equipment/VendorLogo";
import usePageMeta from "../seo/usePageMeta";
import { catalogIndexJsonLd } from "../seo/equipmentSchema";
import { SHOW_EQUIPMENT_CATALOG } from "../featureFlags";
import { COMPARE_PAIRS, TYPE_CATEGORIES } from "../data/catalog";
import { compareTitle } from "./EquipmentCompare";
import { ROBOTS } from "../data/robots";
import {
  PAYLOAD_BANDS,
  SORTS,
  categoryCount,
  displaySpec,
  machineVisible,
  navigationOptions,
  sortMachines,
  toggleValue,
  vendorOptions,
} from "../data/equipmentIndex";

const INITIAL_FILTERS = {
  types: [],
  vendors: [],
  payloadBands: [],
  navigation: [],
};

const FULL_COLUMNS = [
  { label: "Payload", value: (robot) => displaySpec(robot.payloadKg, "kg") },
  { label: "Type", value: (robot) => displaySpec(robot.typeLabel) },
  { label: "Navigation", value: (robot) => displaySpec(robot.navigation) },
  { label: "Top speed", value: (robot) => displaySpec(robot.maxSpeedMs, "m/s") },
  { label: "Minimum aisle", value: (robot) => displaySpec(robot.minAisleMm, "mm") },
];

const VENDOR_FILTER_LABELS = {
  "VisionNav Robotics": "VisionNav",
  "OTTO by Rockwell Automation": "OTTO",
  "SEER Robotics": "SEER",
  "BluePath Robotics": "BluePath",
};

function filterLabel(option) {
  const label = typeof option === "string" ? option : option.label;
  return VENDOR_FILTER_LABELS[label] || label;
}

function FilterDisclosure({ legend, options, selected, onToggle }) {
  return (
    <details className="group border-b border-white/15">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 py-2 text-sm text-white [&::-webkit-details-marker]:hidden">
        <span>{legend}</span>
        <span className="text-xs text-white/50">
          {selected.length ? selected.length : <span aria-hidden="true" className="inline-block transition-transform group-open:rotate-90">›</span>}
        </span>
      </summary>
      <div className="flex flex-wrap gap-1.5 pb-3">
        {options.map((option) => {
          const id = typeof option === "string" ? option : option.id;
          const pressed = selected.includes(id);
          return (
            <button
              key={id}
              type="button"
              aria-pressed={pressed}
              onClick={() => onToggle(id)}
              className={`border px-2 py-1 text-left text-xs leading-snug ${pressed ? "border-white bg-white text-gray-900" : "border-white/25 text-white/80 hover:border-white/60"}`}
            >
              {filterLabel(option)}
            </button>
          );
        })}
      </div>
    </details>
  );
}

function StaticComparisonTable({ robots }) {
  return (
    <table className="w-full border-collapse text-left text-sm text-gray-700">
      <thead>
        <tr className="border-b border-gray-200">
          <th scope="col" className="py-3 pr-4 font-semibold text-gray-900">Machine</th>
          <th scope="col" className="py-3 pr-4 font-semibold text-gray-900">Type</th>
          <th scope="col" className="py-3 pr-4 font-semibold text-gray-900">Payload</th>
          <th scope="col" className="py-3 pr-4 font-semibold text-gray-900">Navigation</th>
          <th scope="col" className="py-3 pr-4 font-semibold text-gray-900">Top speed</th>
          <th scope="col" className="py-3 pr-4 font-semibold text-gray-900">Minimum aisle</th>
        </tr>
      </thead>
      <tbody>
        {robots.map((robot) => (
          <tr key={robot.slug} className="border-b border-gray-200">
            <th scope="row" className="py-3 pr-4 font-medium text-gray-900">
              <Link to={`/catalog/${robot.slug}`} className="underline decoration-gray-300 underline-offset-4">
                {robot.model}
              </Link>
            </th>
            <td className="py-3 pr-4">{displaySpec(robot.typeLabel)}</td>
            <td className="py-3 pr-4">{displaySpec(robot.payloadKg, "kg")}</td>
            <td className="py-3 pr-4">{displaySpec(robot.navigation)}</td>
            <td className="py-3 pr-4">{displaySpec(robot.maxSpeedMs, "m/s")}</td>
            <td className="py-3 pr-4">{displaySpec(robot.minAisleMm, "mm")}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SideBySideTable({ robots }) {
  return (
    <div className="mt-4 overflow-x-auto">
      <table className="w-full min-w-[36rem] border-collapse text-left text-sm text-gray-700">
        <thead>
          <tr className="border-b border-gray-200">
            <th scope="col" className="w-36 py-3 pr-6 font-semibold text-gray-900">Specification</th>
            {robots.map((robot) => (
              <th key={robot.slug} scope="col" className="py-3 pr-6 font-semibold text-gray-900">
                {robot.model}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {FULL_COLUMNS.map((column) => (
            <tr key={column.label} className="border-b border-gray-200">
              <th scope="row" className="py-3 pr-6 align-top font-medium text-gray-500">{column.label}</th>
              {robots.map((robot) => (
                <td key={robot.slug} className="py-3 pr-6 align-top">{column.value(robot)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function EquipmentIndex() {
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [sort, setSort] = useState("catalogue");
  const [selected, setSelected] = useState([]);
  const [focusSlug, setFocusSlug] = useState(ROBOTS[0].slug);
  const [motionOn, setMotionOn] = useState(false);

  const vendors = vendorOptions(ROBOTS);
  const navigation = navigationOptions(ROBOTS);
  const ordered = sortMachines(ROBOTS, sort);
  const visibleOrdered = ordered.filter((robot) => machineVisible(robot, filters));
  const focused = visibleOrdered.find((robot) => robot.slug === focusSlug) || visibleOrdered[0] || ordered[0];
  const selectedRobots = selected
    .map((slug) => ROBOTS.find((robot) => robot.slug === slug))
    .filter(Boolean);

  function setFilterList(key, value) {
    setFilters((current) => ({ ...current, [key]: toggleValue(current[key], value) }));
  }

  function toggleSelected(slug) {
    setSelected((current) => {
      if (current.includes(slug)) return current.filter((item) => item !== slug);
      if (current.length >= 3) return current;
      return [...current, slug];
    });
  }

  function focusRobot(slug) {
    if (slug !== focusSlug) setMotionOn(true);
    setFocusSlug(slug);
  }

  function stepFocus(direction) {
    const list = visibleOrdered.length ? visibleOrdered : ordered;
    const index = Math.max(0, list.findIndex((robot) => robot.slug === focused.slug));
    const next = list[(index + direction + list.length) % list.length];
    focusRobot(next.slug);
  }

  return (
    <div className="bg-white text-gray-900">
      {usePageMeta({
        title: "AMR & AGV Equipment Catalog | MAG",
        description: "AMRs, AGVs and autonomous forklifts for manufacturing plants, compared by payload, navigation, aisle width and charging.",
        path: "/catalog",
        noindex: !SHOW_EQUIPMENT_CATALOG,
        jsonLd: catalogIndexJsonLd(ROBOTS),
      })}

      <div className="mx-auto max-w-[1600px] px-4 py-6 sm:px-6">
        <header className="flex flex-col gap-4 border-b border-gray-200 pb-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <p className="page-eyebrow">Catalog</p>
            <h1 className="mt-2 text-3xl font-bold tracking-tight">
              AMRs and AGVs for Manufacturing
            </h1>
            <p className="mt-2 max-w-3xl text-base leading-relaxed text-gray-600">
              Autonomous mobile robots, autonomous forklifts and tow units for automotive and industrial plants. Filter by payload, navigation method or the action.
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
              Sort
              <select
                className="mt-2 block border border-gray-300 bg-white px-3 py-2 text-sm font-normal normal-case tracking-normal text-gray-900"
                value={sort}
                onChange={(event) => setSort(event.target.value)}
              >
                {SORTS.map((option) => (
                  <option key={option.id} value={option.id}>{option.label}</option>
                ))}
              </select>
            </label>
          </div>
        </header>

        <div className="mt-4 grid items-start gap-6 lg:grid-cols-[250px_minmax(0,1fr)]">
          <aside className="order-2 border border-gray-900 bg-gray-900 p-4 text-white lg:sticky lg:top-24 lg:col-start-1 lg:row-span-2 lg:row-start-1 lg:max-h-[calc(100vh-7rem)] lg:self-start lg:overflow-y-auto">
            <section>
              <h2 className="text-xs font-semibold uppercase tracking-[0.22em] text-white/55">Action</h2>
              <ul className="mt-2">
                {TYPE_CATEGORIES.map((category) => {
                  const count = categoryCount(ROBOTS, category);
                  const countLabel = `${count} ${count === 1 ? "machine" : "machines"}`;
                  const pressed = filters.types.includes(category.id);
                  const detail = category.typeLabels.length > 1 ? category.typeLabels.join(", ") : category.label;
                  return (
                    <li key={category.id}>
                      <button
                        type="button"
                        aria-pressed={pressed}
                        aria-label={`${category.label}, ${countLabel}. ${detail}`}
                        title={detail}
                        onClick={() => setFilterList("types", category.id)}
                        className={`flex w-full items-baseline justify-between gap-3 px-2 py-1.5 text-left text-sm ${pressed ? "bg-white text-gray-900" : "text-white hover:bg-white/10"}`}
                      >
                        <span>{category.label}</span>
                        <span className={`text-xs ${pressed ? "text-gray-500" : "text-white/50"}`}>{count}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>

            <section className="mt-6">
              <h2 className="text-xs font-semibold uppercase tracking-[0.22em] text-white/55">Filter</h2>
              <div className="mt-2">
                <FilterDisclosure legend="Vendor" options={vendors} selected={filters.vendors} onToggle={(value) => setFilterList("vendors", value)} />
                <FilterDisclosure legend="Payload" options={PAYLOAD_BANDS} selected={filters.payloadBands} onToggle={(value) => setFilterList("payloadBands", value)} />
                <FilterDisclosure legend="Navigation" options={navigation} selected={filters.navigation} onToggle={(value) => setFilterList("navigation", value)} />
              </div>
            </section>
          </aside>

          <div className="order-1 flex flex-col gap-4 lg:col-start-2 lg:row-start-1">
          <section className="relative overflow-hidden border border-gray-200 bg-white lg:min-h-[36rem]">
            <div key={focused.slug} className={motionOn ? "equipment-focus-shift" : undefined}>
              <div className="equipment-plate equipment-stage-plate absolute inset-0" aria-hidden="true" />
              <div className="relative z-10 flex items-end justify-between gap-4 p-5 sm:p-7">
                <div className="equipment-copy">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gray-500">In focus</p>
                  <div className="mt-3 flex items-center gap-4">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        aria-label="Previous"
                        onClick={() => stepFocus(-1)}
                        className="flex h-10 w-10 items-center justify-center border border-gray-300 bg-white text-lg hover:bg-gray-50"
                      >
                        ←
                      </button>
                      <button
                        type="button"
                        aria-label="Next"
                        onClick={() => stepFocus(1)}
                        className="flex h-10 w-10 items-center justify-center border border-gray-300 bg-white text-lg hover:bg-gray-50"
                      >
                        →
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="equipment-copy relative z-10 max-w-sm px-5 pb-6 sm:px-7">
                <VendorLogo slug={focused.vendorSlug} className="h-8 w-auto max-w-[11rem] object-contain object-left" />
                <p className="mt-2 text-4xl font-bold tracking-tight sm:text-5xl">{focused.model}</p>
                <p className="mt-2 text-sm text-gray-600">{focused.typeLabel}</p>
              </div>

              <div className={`equipment-tone-${focused.vendorSlug} pointer-events-none relative z-10 mx-auto h-72 w-72 sm:h-80 sm:w-80 lg:absolute lg:left-[58%] lg:top-1/2 lg:h-[22rem] lg:w-[22rem] lg:-translate-x-1/2 lg:-translate-y-1/2`}>
                <span className="equipment-ring equipment-ring-a" aria-hidden="true" />
                <span className="equipment-ring equipment-ring-b" aria-hidden="true" />
                <span className="equipment-ring equipment-ring-c" aria-hidden="true" />
                <img
                  src={focused.image.src}
                  alt={focused.image.alt}
                  width="1200"
                  height="900"
                  fetchPriority="high"
                  className="equipment-machine relative z-10 h-full w-full object-contain"
                />
              </div>

              <div className="equipment-copy equipment-glass relative z-20 mx-4 mb-4 p-5 sm:mx-5 lg:absolute lg:bottom-6 lg:right-6 lg:mx-0 lg:mb-0 lg:w-[320px]">
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between gap-4 border-b border-gray-900/10 pb-2">
                    <dt className="text-gray-500">Payload</dt>
                    <dd>{displaySpec(focused.payloadKg, "kg")}</dd>
                  </div>
                  <div className="flex justify-between gap-4 border-b border-gray-900/10 pb-2">
                    <dt className="text-gray-500">Top speed</dt>
                    <dd>{displaySpec(focused.maxSpeedMs, "m/s")}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-500">Navigation</dt>
                    <dd className="text-right">{displaySpec(focused.navigation)}</dd>
                  </div>
                </dl>
                <p className="mt-4 text-sm leading-relaxed text-gray-600">{focused.summary}</p>
                <Link
                  to={`/catalog/${focused.slug}`}
                  className="mt-5 inline-block text-sm underline decoration-gray-300 underline-offset-4"
                >
                  {`${focused.displayName} specifications`}
                </Link>
              </div>
            </div>
          </section>
          {selectedRobots.length > 0 ? (
            <section className="border border-gray-200 bg-white px-4 py-4">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="mr-1 text-sm font-semibold">Comparison</h2>
                {selectedRobots.map((robot) => (
                  <button
                    key={robot.slug}
                    type="button"
                    onClick={() => toggleSelected(robot.slug)}
                    aria-label={`Remove ${robot.displayName}`}
                    className="inline-flex items-center gap-1.5 border border-gray-300 px-2 py-0.5 text-xs text-gray-800 hover:border-gray-900"
                  >
                    {robot.model}
                    <span aria-hidden="true">×</span>
                  </button>
                ))}
              </div>
              <SideBySideTable robots={selectedRobots} />
            </section>
          ) : null}
          </div>

          <ul className="order-3 grid gap-4 sm:grid-cols-2 lg:col-start-2 lg:row-start-2 xl:grid-cols-3">
            {ordered.map((robot) => {
              const visible = machineVisible(robot, filters);
              const isSelected = selected.includes(robot.slug);
              const isFocused = focused && robot.slug === focused.slug;
              return (
                <li
                  key={robot.slug}
                  hidden={!visible}
                  className={`relative border bg-white ${isFocused ? "border-gray-900" : "border-gray-200"}`}
                >
                  <label className="absolute right-3 top-3 z-10 border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700">
                    <input
                      type="checkbox"
                      aria-label={`Compare ${robot.displayName}`}
                      checked={isSelected}
                      disabled={!isSelected && selected.length >= 3}
                      onChange={() => {
                        toggleSelected(robot.slug);
                        focusRobot(robot.slug);
                      }}
                    />
                    <span className="ml-1">Compare</span>
                  </label>
                  <Link
                    to={`/catalog/${robot.slug}`}
                    className="block px-3 pt-12 text-left hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-900"
                  >
                    <span className="mx-auto flex aspect-square w-full items-center justify-center px-3 pt-2 pb-1">
                      <img
                        src={robot.image.src}
                        alt={robot.image.alt}
                        width="1200"
                        height="900"
                        loading="lazy"
                        decoding="async"
                        className="max-h-full max-w-full object-contain"
                      />
                    </span>
                    <span className="block px-1 pb-4">
                      <VendorLogo slug={robot.vendorSlug} className="mt-4 h-7 w-auto max-w-[9rem] object-contain object-left" />
                      <span className="mt-1 block text-sm text-gray-500">{robot.typeLabel}</span>
                      <span className="mt-1 block text-xl font-semibold text-gray-900">{robot.model}</span>
                      <span className="mt-3 block text-sm text-gray-700">{`Payload: ${displaySpec(robot.payloadKg, "kg")}`}</span>
                      <span className="block text-sm text-gray-700">{`Top speed: ${displaySpec(robot.maxSpeedMs, "m/s")}`}</span>
                      <span className="block text-sm text-gray-700">{`Navigation: ${displaySpec(robot.navigation)}`}</span>
                      {robot.magDeployed ? (
                        <span className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-gray-900">
                          <span className="status-led" aria-hidden="true" />
                          MAG deployed
                        </span>
                      ) : null}
                      <span className="mt-3 block text-sm leading-relaxed text-gray-600">{robot.summary}</span>
                    </span>
                  </Link>
                </li>
              );
            })}
            <li className="flex border border-gray-900 bg-gray-900 text-white">
              <div className="flex h-full w-full items-center px-6 py-8">
                <p className="text-2xl font-semibold tracking-tight">Coming Soon</p>
              </div>
            </li>
          </ul>
        </div>

        <nav aria-label="Equipment comparisons" className="mt-10 border border-gray-200 bg-white px-6 py-6">
          <h2 className="text-sm font-semibold">Comparison</h2>
          <ul className="mt-3 space-y-2">
            {COMPARE_PAIRS.map((pair) => {
              const left = ROBOTS.find((robot) => robot.slug === pair.left);
              const right = ROBOTS.find((robot) => robot.slug === pair.right);
              if (!left || !right) return null;
              const label = compareTitle(left.displayName, right.displayName).replace(/ \| MAG$/, "");
              return (
                <li key={pair.slug}>
                  <Link
                    to={`/catalog/compare/${pair.slug}`}
                    className="text-sm underline decoration-gray-300 underline-offset-4"
                  >
                    {label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        <section className="mt-10 border border-gray-200 bg-white">
          <details>
            <summary className="cursor-pointer px-6 py-4 text-sm font-semibold text-gray-900">
              Specification table
            </summary>
            <div className="overflow-x-auto px-6 pb-6">
              <StaticComparisonTable robots={ROBOTS} />
            </div>
          </details>
        </section>

        <section className="mt-10 border border-gray-200 bg-white px-6 py-10">
          <h2 className="text-2xl font-semibold">The fleet, matched to the plant</h2>
          <p className="mt-3 max-w-2xl text-gray-600">
            The machine is matched to the plant, the fleet is installed, and support continues after launch.
          </p>
          <Link to="/contact" className="btn-primary-dark mt-6 inline-block">
            Contact MAG
          </Link>
        </section>
      </div>
    </div>
  );
}
